import { Button, Skeleton, Typography } from "@/components/atoms";
import {
  ChevronLeftBadgeIcon,
  ChevronRightBadgeIcon,
  NoSlotsAvailableIcon,
} from "@/components/atoms/icons";
import {
  AppointmentConfirmedDialog,
  AppointmentSuccessDialog,
  FullScreenLoader,
  OkDialog,
} from "@/components/molecules";
import { TextHeader } from "@/components/molecules/header/TextHeader";
import {
  appointmentService,
  extractUpcomingAppointment,
  mapSlotTimesFromResponse,
} from "@/core/api/services/appointmentService";
import type { UIColorTokens } from "@/core/constants/uiColors";
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from "@/core/constants/dynatraceCatalog";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import {
  // useSalesAgentContact, // Sales agent contact hidden for now
  useTranslation,
  useUIColors,
} from "@/core/hooks";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { logger } from "@/core/logger";
import { isPhoneScreen } from "@/core/utils/responsive";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { useAppStore } from "@/core/stores/appStore";
import {
  mapProfileDetailsToUserPatch,
  profileService,
} from "@/modules/auth/services/profileService";
import { useAppointmentLabels } from "@/modules/book-appointment/hooks/useAppointmentLabels";
import type {
  AppointmentSlotTime,
  AppointmentSlotsLoadState,
} from "@/modules/book-appointment/types/appointment.types";
import {
  isoToLocalDate,
  resolveAppointmentBookingDateIsos,
  toIsoDate,
} from "@/modules/book-appointment/utils/slotDates";
import { withPastSlotsDisabled } from "@/modules/book-appointment/utils/slotAvailability";
import {
  getRelativeDayBadge,
  mapUpcomingToAppStoreAppointment,
} from "@/modules/home/components/HomeScreenMobileAdhaMobile";
import { router, useLocalSearchParams } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

// const DAYS_TO_SHOW = 14;
const DATE_CHIP_WIDTH = 80;
const DATE_CHIP_GAP = 12;
const DATE_CHIP_STRIDE = DATE_CHIP_WIDTH + DATE_CHIP_GAP;
const DATE_SCROLL_STEP = DATE_CHIP_STRIDE * 3;
const TIME_SLOT_GAP = 12;
const TIME_SLOT_COLUMNS = 3;
/** Match web AppointmentSlotDialog desktop time chip width. */
const TIME_SLOT_CHIP_WIDTH = 97;
const TIME_SLOT_SKELETON_COUNT = 6;
const TIME_SLOT_SKELETON_KEYS = [
  "slot-skeleton-morning-1",
  "slot-skeleton-morning-2",
  "slot-skeleton-afternoon-1",
  "slot-skeleton-afternoon-2",
  "slot-skeleton-evening-1",
  "slot-skeleton-evening-2",
] as const;
interface DateSlot {
  date: Date;
  dayLabel: string;
  subLabel: string;
}

function parseAppointmentDate(displayDate: string): Date | null {
  const trimmed = displayDate.trim();
  if (!trimmed) {
    return null;
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
}

function findDateSlotIndex(slots: DateSlot[], displayDate?: string): number {
  if (!displayDate) {
    return 0;
  }
  const target = parseAppointmentDate(displayDate);
  if (!target) {
    return 0;
  }
  const index = slots.findIndex(
    (slot) =>
      slot.date.getFullYear() === target.getFullYear() &&
      slot.date.getMonth() === target.getMonth() &&
      slot.date.getDate() === target.getDate(),
  );
  return Math.max(index, 0);
}

function getTimeSlotWidth(gridWidth: number): number | undefined {
  if (gridWidth <= 0) {
    return undefined;
  }
  return (
    (gridWidth - TIME_SLOT_GAP * (TIME_SLOT_COLUMNS - 1)) / TIME_SLOT_COLUMNS
  );
}

function getTimeChipWidthStyle(
  isPhoneTimeGrid: boolean,
  timeSlotWidth: number | undefined,
): { width: number } | typeof styles.timeCardPhone {
  if (!isPhoneTimeGrid) {
    return { width: TIME_SLOT_CHIP_WIDTH };
  }
  if (timeSlotWidth != null) {
    return { width: timeSlotWidth };
  }
  return styles.timeCardPhone;
}

function getTimeChipSkeletonWidth(
  isPhoneTimeGrid: boolean,
  timeSlotWidth: number | undefined,
): number | string {
  if (!isPhoneTimeGrid) {
    return TIME_SLOT_CHIP_WIDTH;
  }
  return timeSlotWidth ?? "31%";
}

function getTimeChipSkeletonStyle(isPhoneTimeGrid: boolean) {
  return isPhoneTimeGrid
    ? styles.timeCardSkeleton
    : styles.timeCardSkeletonFixed;
}

interface AppointmentSlotsGridProps {
  slotsLoadState: AppointmentSlotsLoadState;
  timeSlots: AppointmentSlotTime[];
  selectedTimeId: string | null;
  onSelectTime: (id: string) => void;
  timeSlotWidth: number | undefined;
  isPhoneTimeGrid: boolean;
  onGridWidthChange: (width: number) => void;
  isRTL: boolean;
  colors: UIColorTokens;
  labels: ReturnType<typeof useAppointmentLabels>;
}

/**
 * "Slot Time" area: skeleton while loading, the no-slots illustration + raise-ticket
 * link when empty, an error message, or the pickable time chips on success. Split out
 * of BookAppointmentScreen to keep that component's cognitive complexity under control
 * (sonar typescript:S3776).
 */
function AppointmentSlotsGrid({
  slotsLoadState,
  timeSlots,
  selectedTimeId,
  onSelectTime,
  timeSlotWidth,
  isPhoneTimeGrid,
  onGridWidthChange,
  isRTL,
  colors,
  labels,
}: Readonly<AppointmentSlotsGridProps>) {
  if (slotsLoadState === "empty") {
    return (
      <View style={styles.noSlotsWrapper}>
        <NoSlotsAvailableIcon size={160} />
        <View
          style={[
            styles.emptySlotsMessageRow,
            isRTL && styles.emptySlotsMessageRowRTL,
          ]}
        >
          <Typography
            variant="body"
            color="highContrast"
            style={styles.slotsStateText}
          >
            {labels.SLOTS_EMPTY}
          </Typography>
          <Pressable
            onPress={() => router.push("/service-request-create" as never)}
          >
            <Typography
              style={[
                styles.raiseSupportTicketText,
                { color: colors.fontErrorText },
              ]}
            >
              {labels.RAISE_SUPPORT_TICKET}
            </Typography>
          </Pressable>
        </View>
      </View>
    );
  }

  const isSkeletonState =
    slotsLoadState === "loading" || slotsLoadState === "idle";

  return (
    <View
      style={[styles.timeGrid, isRTL && styles.timeGridRTL]}
      onLayout={(event) => onGridWidthChange(event.nativeEvent.layout.width)}
    >
      {isSkeletonState &&
        TIME_SLOT_SKELETON_KEYS.slice(0, TIME_SLOT_SKELETON_COUNT).map(
          (skeletonKey) => (
            <Skeleton
              key={skeletonKey}
              width={getTimeChipSkeletonWidth(isPhoneTimeGrid, timeSlotWidth)}
              height={44}
              borderRadius={12}
              style={getTimeChipSkeletonStyle(isPhoneTimeGrid)}
            />
          ),
        )}
      {slotsLoadState === "error" && (
        <View style={styles.slotsStateBlock}>
          <Typography
            variant="body"
            color="secondary"
            style={styles.slotsStateText}
          >
            {labels.SLOTS_ERROR}
          </Typography>
        </View>
      )}
      {slotsLoadState === "success" &&
        timeSlots.map((slot) => {
          const isSelected = selectedTimeId === slot.id;
          const isDisabled = slot.disabled === true;
          return (
            <TouchableOpacity
              key={slot.id}
              disabled={isDisabled}
              onPress={() => onSelectTime(slot.id)}
              style={[
                styles.timeCard,
                getTimeChipWidthStyle(isPhoneTimeGrid, timeSlotWidth),
                isDisabled && styles.timeCardDisabled,
                {
                  backgroundColor: isSelected
                    ? colors.buttonDefault
                    : colors.backgroundLevel1,
                  borderColor: isSelected
                    ? colors.buttonDefault
                    : colors.outlineInactive,
                },
              ]}
            >
              <Typography
                style={[
                  styles.slotLabelText,
                  {
                    color: isSelected
                      ? colors.backgroundLevel1
                      : colors.fontPrimary,
                    opacity: isDisabled ? 0.4 : 1,
                  },
                ]}
              >
                {slot.label}
              </Typography>
            </TouchableOpacity>
          );
        })}
    </View>
  );
}

export function BookAppointmentScreen() {
  useCatalogDynatraceView(
    ALMETLAA_SCREEN.APPOINTMENT,
    ALMETLAA_ACTION.APPOINTMENT.PAGE_VIEW,
    true,
  );
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useI18nContext();
  // Only for what's shown on screen (date chips, confirmation dialog) — the
  // dateLabel persisted to the store below stays "en-GB" on purpose, since
  // parseAppointmentDate's reschedule matching re-parses it with `new Date(...)`,
  // which can't read an Arabic month name back into a valid Date.
  const dateLocale = currentLanguage === "ar" ? "ar-AE" : "en-GB";
  const labels = useAppointmentLabels();
  const insets = useSafeAreaInsets();
  const {
    propertyId,
    eventId: eventIdParam,
    mode,
    unitLabel,
  } = useLocalSearchParams<{
    propertyId?: string;
    eventId?: string;
    mode?: "book" | "reschedule";
    unitLabel?: string;
  }>();
  const isReschedule = mode === "reschedule";
  const screenTitle = isReschedule
    ? t("bookAppointment.reschedule")
    : t("bookAppointment.bookAppointment");
  const appointment = useAppStore((state) => state.appointment);
  // Sales agent contact hidden for now
  // const salesAgent = useSalesAgentContact();
  const appointmentBookingStartDate = useAppStore(
    (state) => state.auth.user?.appointmentBookingStartDate,
  );
  const appointmentBookingEndDate = useAppStore(
    (state) => state.auth.user?.appointmentBookingEndDate,
  );

  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [selectedTimeId, setSelectedTimeId] = useState<string | null>(null);
  const [timeSlots, setTimeSlots] = useState<AppointmentSlotTime[]>([]);
  const [slotsLoadState, setSlotsLoadState] =
    useState<AppointmentSlotsLoadState>("idle");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const [appointmentConfirmedVisible, setAppointmentConfirmedVisible] =
    useState(false);
  const [appointmentSuccessVisible, setAppointmentSuccessVisible] =
    useState(false);
  const [timeGridWidth, setTimeGridWidth] = useState(0);
  const dateScrollRef = useRef<ScrollView>(null);
  const dateScrollOffset = useRef(0);
  const [currentScrollX, setCurrentScrollX] = useState(0);
  const didApplyRescheduleDate = useRef(false);
  const slotsRequestIdRef = useRef(0);
  const agentRefreshedRef = useRef(false);

  const dateSlots: DateSlot[] = useMemo(() => {
    const isoDates = resolveAppointmentBookingDateIsos({
      startDate: appointmentBookingStartDate,
      endDate: appointmentBookingEndDate,
      months: 3,
    });
    const today = new Date();
    const todayIso = toIsoDate(today);
    const tomorrow = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + 1,
    );
    const tomorrowIso = toIsoDate(tomorrow);

    const slots: DateSlot[] = [];
    for (const iso of isoDates) {
      const date = isoToLocalDate(iso);
      if (!date) {
        continue;
      }
      const dayLabel = new Intl.DateTimeFormat(dateLocale, {
        day: "numeric",
        month: "short",
      }).format(date);
      let subLabel = "";
      if (iso === todayIso) {
        subLabel = t("bookAppointment.today");
      } else if (iso === tomorrowIso) {
        subLabel = t("bookAppointment.tomorrow");
      }
      slots.push({ date, dayLabel, subLabel });
    }
    return slots;
  }, [
    appointmentBookingEndDate,
    appointmentBookingStartDate,
    dateLocale,
    t,
  ]);

  const dateWindowKey = useMemo(
    () => dateSlots.map((slot) => toIsoDate(slot.date)).join("|"),
    [dateSlots],
  );
  const dateWindowKeyRef = useRef(dateWindowKey);

  useEffect(() => {
    if (dateWindowKeyRef.current === dateWindowKey) {
      return;
    }
    dateWindowKeyRef.current = dateWindowKey;
    didApplyRescheduleDate.current = false;
    if (!isReschedule) {
      setSelectedDateIndex(0);
    }
  }, [dateWindowKey, isReschedule]);

  useEffect(() => {
    if (
      !isReschedule ||
      didApplyRescheduleDate.current ||
      dateSlots.length === 0
    ) {
      return;
    }
    if (!appointment?.date) {
      return;
    }
    const index = findDateSlotIndex(dateSlots, appointment.date);
    didApplyRescheduleDate.current = true;
    setSelectedDateIndex(index);
    const offset = index * DATE_CHIP_STRIDE;
    dateScrollOffset.current = offset;
    const scrollToSelected = () => {
      // Always positive: the strip's coordinates are LTR in both languages
      // (see dateScrollViewRTL) — a negative x was clamped to 0 in Arabic.
      dateScrollRef.current?.scrollTo({ x: offset, animated: false });
    };
    const rafId = requestAnimationFrame(scrollToSelected);
    const timeoutId = setTimeout(scrollToSelected, 50);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [appointment?.date, dateSlots, isReschedule]);

  const selectedDateIso = useMemo(() => {
    const date = dateSlots[selectedDateIndex]?.date;
    return date ? toIsoDate(date) : "";
  }, [dateSlots, selectedDateIndex]);

  const loadSlotTimes = useCallback(
    async (date?: string) => {
      const requestId = ++slotsRequestIdRef.current;
      setSlotsLoadState("loading");
      setTimeSlots([]);

      try {
        // sales_agent_id is cached in the store once at ADHA login and never
        // refreshed since — but Salesforce can reassign a customer's sales
        // agent between logins, so a session that's been open a while can
        // silently query slots against a stale, no-longer-current agent
        // (whose calendar looks empty) instead of the one actually handling
        // this customer now. Refresh it fresh from profile-details once per
        // screen visit, not just when it's missing outright.
        if (!agentRefreshedRef.current) {
          agentRefreshedRef.current = true;
          try {
            const details = await profileService.getProfileDetails();
            const patch = mapProfileDetailsToUserPatch(details);
            useAppStore.getState().setProfile(patch);
          } catch (profileError) {
            logger.warn(
              "BookAppointmentScreen:refresh profile-details for sales_agent_id failed",
              profileError,
            );
          }
        }

        if (!date) {
          if (requestId !== slotsRequestIdRef.current) {
            return;
          }
          setTimeSlots([]);
          setSlotsLoadState("empty");
          return;
        }

        logger.info("BookAppointmentScreen:API getSlots — calling", {
          propertyId,
          date,
        });
        const response = await appointmentService.getSlots({
          ...(propertyId ? { unitId: propertyId } : {}),
          ...(date ? { date } : {}),
        });
        logger.info("BookAppointmentScreen:API getSlots — success", {
          response,
        });

        if (requestId !== slotsRequestIdRef.current) {
          return;
        }

        const withPastDisabled = withPastSlotsDisabled(
          mapSlotTimesFromResponse(response),
        );
        // Reschedule: the slot matching the currently booked appointment_id
        // must stay disabled/blurred, same treatment as an unavailable slot —
        // user has to pick a different slot to reschedule into.
        const mapped =
          isReschedule && appointment?.appointmentId
            ? withPastDisabled.map((slot) =>
                slot.id === appointment.appointmentId
                  ? { ...slot, disabled: true }
                  : slot,
              )
            : withPastDisabled;
        if (mapped.length > 0) {
          setTimeSlots(mapped);
          setSlotsLoadState("success");
          return;
        }

        setTimeSlots([]);
        setSlotsLoadState("empty");
      } catch (error) {
        logger.error("BookAppointmentScreen:API getSlots — failed", error);
        if (requestId !== slotsRequestIdRef.current) {
          return;
        }
        setTimeSlots([]);
        setSlotsLoadState("error");
      }
    },
    [propertyId, isReschedule, appointment?.appointmentId],
  );

  useEffect(() => {
    void loadSlotTimes(selectedDateIso);
  }, [loadSlotTimes, selectedDateIso]);

  useEffect(() => {
    if (slotsLoadState !== "success" || !selectedTimeId) {
      return;
    }
    if (!timeSlots.some((slot) => slot.id === selectedTimeId)) {
      setSelectedTimeId(null);
    }
  }, [slotsLoadState, timeSlots, selectedTimeId]);

  const handleDateSelect = useCallback(
    (index: number) => {
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.APPOINTMENT,
        actionName: ALMETLAA_ACTION.APPOINTMENT.DATE_SELECT_CLICK,
        eventType: "action",
      });
      setSelectedDateIndex(index);
      setSelectedTimeId(null);
      const offset = index * DATE_CHIP_STRIDE;
      dateScrollOffset.current = offset;
      setCurrentScrollX(offset);
      dateScrollRef.current?.scrollTo({ x: offset, animated: true });
    },
    [],
  );

  const selectedTimeLabel =
    timeSlots.find((slot) => slot.id === selectedTimeId)?.label ?? "";
  const slotsReady = slotsLoadState === "success" && timeSlots.length > 0;
  const isPhoneTimeGrid = isPhoneScreen();
  const timeSlotWidth = isPhoneTimeGrid
    ? getTimeSlotWidth(timeGridWidth)
    : TIME_SLOT_CHIP_WIDTH;

  const scrollDates = (direction: 1 | -1) => {
    const currentIndex = Math.round(
      dateScrollOffset.current / DATE_CHIP_STRIDE,
    );
    const maxIndex = Math.max(0, dateSlots.length - 1);
    const nextIndex = Math.min(
      maxIndex,
      Math.max(0, currentIndex + direction * 3),
    );
    if (nextIndex === currentIndex) {
      return;
    }
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.APPOINTMENT,
      actionName: ALMETLAA_ACTION.APPOINTMENT.DATE_SCROLL_CLICK,
      eventType: "action",
    });
    const targetOffset = nextIndex * DATE_CHIP_STRIDE;
    dateScrollOffset.current = targetOffset;
    setCurrentScrollX(targetOffset);
    dateScrollRef.current?.scrollTo({ x: targetOffset, animated: true });
  };

  // Final step for both flows: persist the appointment and return to the
  // screen the user started the booking from (property/unit details).
  // Always re-fetches GET /upcoming instead of echoing the locally-picked date/time,
  // so the upcoming appointment card only ever shows what the backend actually stored.
  const finalizeAppointment = useCallback(() => {
    setAppointmentConfirmedVisible(false);
    setAppointmentSuccessVisible(false);

    const selectedDate = dateSlots[selectedDateIndex];
    const dateLabel =
      selectedDate?.date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }) ?? "";
    const resolvedUnitLabel =
      (typeof unitLabel === "string" ? unitLabel : "") ||
      appointment?.unitLabel ||
      "";

    useAppStore.getState().setAppointment({
      propertyId: propertyId ?? appointment?.propertyId ?? "",
      date: dateLabel || appointment?.date || "",
      time: selectedTimeLabel || appointment?.time || "",
      // subLabel only covers Today/Tomorrow (see dateSlots above) — any date
      // further out fell through to the generic "Upcoming Appointment" text
      // for the brief moment before the background getUpcoming() sync (which
      // uses this same getRelativeDayBadge) corrected it. Compute it the same
      // way here so there's no visible flash of the wrong label.
      // An empty result is valid (no badge beyond Today/Tomorrow, same as
      // web), so only fall back when there is no selected date at all.
      badgeLabel: selectedDateIso
        ? getRelativeDayBadge(selectedDateIso, t)
        : appointment?.badgeLabel ||
          t("bookAppointment.upcomingAppointment"),
      ...(selectedTimeId ? { appointmentId: selectedTimeId } : {}),
      ...(appointment?.eventId ? { eventId: appointment.eventId } : {}),
      ...(eventIdParam?.trim() ? { eventId: eventIdParam.trim() } : {}),
      ...(resolvedUnitLabel ? { unitLabel: resolvedUnitLabel } : {}),
      ...(appointment?.image ? { image: appointment.image } : {}),
    });

    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace({ pathname: "/(auth)/(home)" });
    }

    void (async () => {
      try {
        logger.info(
          "BookAppointmentScreen:API getUpcoming (finalize) — calling",
          {
            propertyId,
          },
        );
        const response = await appointmentService.getUpcoming({
          ...(propertyId ? { unitId: propertyId } : {}),
        });
        logger.info(
          "BookAppointmentScreen:API getUpcoming (finalize) — success",
          {
            response,
          },
        );
        const dto = extractUpcomingAppointment(response);
        const mapped = dto
          ? mapUpcomingToAppStoreAppointment(dto, propertyId ?? "", t, isRTL)
          : null;
        if (mapped) {
          useAppStore.getState().setAppointment(mapped);
        }
      } catch (error) {
        logger.error(
          "BookAppointmentScreen:API getUpcoming (finalize) — failed",
          error,
        );
      }
    })();
  }, [
    appointment?.badgeLabel,
    appointment?.date,
    appointment?.eventId,
    appointment?.image,
    appointment?.propertyId,
    appointment?.time,
    appointment?.unitLabel,
    dateSlots,
    eventIdParam,
    isRTL,
    propertyId,
    selectedDateIndex,
    selectedDateIso,
    selectedTimeId,
    selectedTimeLabel,
    t,
    unitLabel,
  ]);

  const handleConfirmedDialogDismissed = useCallback(() => {
    finalizeAppointment();
  }, [finalizeAppointment]);

  // Same as web `useBookAppointmentFlow.submitSlot` (reschedule branch):
  // resolve event_id from the route param, falling back to GET /upcoming.
  // Strictly uses `event_id` — never falls back to appointment_id/id.
  const resolveRescheduleEventId = useCallback(async (): Promise<string> => {
    const fromParam = eventIdParam?.trim() || "";
    if (fromParam) {
      return fromParam;
    }

    logger.info(
      "BookAppointmentScreen:API getUpcoming (resolve eventId) — calling",
      { propertyId },
    );
    const upcomingResponse = await appointmentService.getUpcoming({
      ...(propertyId ? { unitId: propertyId } : {}),
    });
    logger.info(
      "BookAppointmentScreen:API getUpcoming (resolve eventId) — success",
      { response: upcomingResponse },
    );
    const dto = extractUpcomingAppointment(upcomingResponse);
    const rawEventId = dto?.event_id;
    return typeof rawEventId === "string" ? rawEventId.trim() : "";
  }, [eventIdParam, propertyId]);

  /** Returns false when reschedule cannot proceed (missing event_id). */
  const submitSelectedSlot = useCallback(async (): Promise<boolean> => {
    if (!selectedTimeId) {
      return false;
    }

    if (!isReschedule) {
      logger.info("BookAppointmentScreen:API scheduleAppointment — calling", {
        appointment_slot_id: selectedTimeId,
        propertyId,
      });
      await appointmentService.scheduleAppointment({
        appointment_slot_id: selectedTimeId,
        ...(propertyId ? { unitId: propertyId } : {}),
      });
      logger.info("BookAppointmentScreen:API scheduleAppointment — success");
      return true;
    }

    const eventId = await resolveRescheduleEventId();
    if (!eventId) {
      setErrorDialogMessage(
        "Unable to reschedule — no existing appointment found.",
      );
      setErrorDialogVisible(true);
      return false;
    }

    // logger.info("BookAppointmentScreen:API updateAppointment — calling", {
    //   action_type: "Rescheduled",
    //   event_id: eventId,
    //   appointment_slot_id: selectedTimeId,
    // });
    await appointmentService.updateAppointment({
      action_type: "Rescheduled",
      event_id: eventId,
      appointment_slot_id: selectedTimeId,
      reason: "Rescheduled by customer",
    });
    // logger.info("BookAppointmentScreen:API updateAppointment — success");
    return true;
  }, [isReschedule, propertyId, resolveRescheduleEventId, selectedTimeId]);

  const handleBookAppointment = useCallback(async () => {
    if (!selectedTimeId || !slotsReady || isSubmitting) {
      return;
    }

    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.APPOINTMENT,
      actionName: ALMETLAA_ACTION.APPOINTMENT.SUBMIT_CLICK,
      eventType: 'action',
    });
    setIsSubmitting(true);
    try {
      const submitted = await submitSelectedSlot();
      if (!submitted) {
        return;
      }

      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.APPOINTMENT,
        actionName: ALMETLAA_ACTION.APPOINTMENT.SUCCESS,
        eventType: 'action',
      });
      setAppointmentConfirmedVisible(true);
    } catch (error) {
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.APPOINTMENT,
        actionName: ALMETLAA_ACTION.APPOINTMENT.ERROR,
        eventType: 'error',
      });
      // logger.error(
      //   "BookAppointmentScreen:API handleBookAppointment — failed",
      //   error,
      // );
      const fallback = isReschedule
        ? labels.RESCHEDULE_ERROR
        : labels.BOOK_ERROR;
      const message =
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : fallback;
      setErrorDialogMessage(message);
      setErrorDialogVisible(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isReschedule,
    isSubmitting,
    selectedTimeId,
    slotsReady,
    submitSelectedSlot,
  ]);

  const currentIndex = Math.round(currentScrollX / DATE_CHIP_STRIDE);
  const canScrollBack = currentIndex > 0;
  const canScrollForward = currentIndex < dateSlots.length - 1;
  // Arabic mirrors the strip (today on the right, later dates to the left),
  // while the chevron pair stays "< >" — so "<" moves forward there.
  const leftChevronDirection: 1 | -1 = isRTL ? 1 : -1;
  const canScrollLeft = isRTL ? canScrollForward : canScrollBack;
  const canScrollRight = isRTL ? canScrollBack : canScrollForward;

  return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
    >
      <TextHeader title={screenTitle} />

      <View style={styles.content}>
        <View style={styles.sectionHeaderRow}>
          <Typography variant="headline" color="primary">
            {t("bookAppointment.slotDate")}
          </Typography>
          <View
            style={[
              styles.dateScrollControls,
              isRTL && styles.dateScrollControlsRTL,
            ]}
          >
            <TouchableOpacity
              onPress={() => scrollDates(leftChevronDirection)}
              disabled={!canScrollLeft}
              style={[styles.scrollButton, !canScrollLeft && { opacity: 0.5 }]}
              accessibilityRole="button"
            >
              <ChevronLeftBadgeIcon size={20} disabled={!canScrollLeft} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => scrollDates(leftChevronDirection === 1 ? -1 : 1)}
              disabled={!canScrollRight}
              style={[styles.scrollButton, !canScrollRight && { opacity: 0.5 }]}
              accessibilityRole="button"
            >
              <ChevronRightBadgeIcon size={20} disabled={!canScrollRight} />
            </TouchableOpacity>
          </View>
        </View>

        <ScrollView
          ref={dateScrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          snapToInterval={DATE_CHIP_STRIDE}
          snapToAlignment="start"
          onScroll={(e) => {
            const x = Math.max(0, e.nativeEvent.contentOffset.x);
            dateScrollOffset.current = x;
            setCurrentScrollX(x);
          }}
          scrollEventThrottle={16}
          contentContainerStyle={[styles.dateList, isRTL && styles.dateListRTL]}
          style={[styles.dateScrollView, isRTL && styles.dateScrollViewRTL]}
        >
          {dateSlots.map((slot, index) => {
            const isSelected = index === selectedDateIndex;
            return (
              <TouchableOpacity
                key={slot.date.toISOString()}
                onPress={() => handleDateSelect(index)}
                style={[
                  styles.dateCard,
                  isRTL ? { transform: [{ scaleX: -1 }] } : undefined,
                  {
                    backgroundColor: isSelected
                      ? colors.buttonDefault
                      : colors.backgroundLevel1,
                    borderColor: isSelected
                      ? colors.buttonDefault
                      : colors.outlineInactive,
                  },
                ]}
              >
                <Typography
                  style={[
                    styles.slotLabelText,
                    {
                      color: isSelected
                        ? colors.backgroundLevel1
                        : colors.fontPrimary,
                    },
                  ]}
                >
                  {slot.dayLabel}
                </Typography>
                {Boolean(slot.subLabel) && (
                  <Typography
                    variant="footnote"
                    style={{
                      color: isSelected
                        ? colors.backgroundLevel1
                        : colors.fontTertiary,
                    }}
                  >
                    {slot.subLabel}
                  </Typography>
                )}
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        <Typography
          variant="headline"
          color="primary"
          style={styles.timeLabelSpacing}
        >
          {t("bookAppointment.slotTime")}
        </Typography>

        <AppointmentSlotsGrid
          slotsLoadState={slotsLoadState}
          timeSlots={timeSlots}
          selectedTimeId={selectedTimeId}
          onSelectTime={(timeId) => {
            trackCatalogEvent({
              screen: ALMETLAA_SCREEN.APPOINTMENT,
              actionName: ALMETLAA_ACTION.APPOINTMENT.SLOT_SELECT_CLICK,
              eventType: 'action',
            });
            setSelectedTimeId(timeId);
          }}
          timeSlotWidth={timeSlotWidth}
          isPhoneTimeGrid={isPhoneTimeGrid}
          onGridWidthChange={(width) =>
            setTimeGridWidth((current) => (current === width ? current : width))
          }
          isRTL={isRTL}
          colors={colors}
          labels={labels}
        />
      </View>

      <View
        style={[
          styles.footer,
          {
            paddingBottom: Math.max(insets.bottom, 16),
            borderTopColor: colors.outlineInactive,
          },
        ]}
      >
        <Button
          title={t("common.cancel")}
          variant="outline"
          onPress={() => {
            trackCatalogEvent({
              screen: ALMETLAA_SCREEN.APPOINTMENT,
              actionName: ALMETLAA_ACTION.APPOINTMENT.SLOT_DIALOG_CANCEL,
              eventType: "action",
            });
            router.back();
          }}
          style={styles.footerButton}
        />
        <Button
          title={
            isReschedule
              ? t("propertyDetails.reschedule")
              : t("bookAppointment.bookAppointment")
          }
          variant="primary"
          disabled={
            !selectedTimeId ||
            !slotsReady ||
            isSubmitting ||
            (isReschedule && selectedTimeId === appointment?.appointmentId)
          }
          onPress={() => {
            void handleBookAppointment();
          }}
          style={styles.footerButton}
          textStyle={{
            color: colors.backgroundLevel1,
          }}
        />
      </View>

      <AppointmentConfirmedDialog
        visible={appointmentConfirmedVisible}
        date={
          dateSlots[selectedDateIndex]?.date.toLocaleDateString(dateLocale, {
            day: "numeric",
            month: "long",
            year: "numeric",
          }) ?? ""
        }
        time={selectedTimeLabel}
        /* Sales agent contact hidden for now
        agentName={salesAgent.name}
        phoneNumber={salesAgent.phone}
        */
        onOk={handleConfirmedDialogDismissed}
      />

      <AppointmentSuccessDialog
        visible={appointmentSuccessVisible}
        autoCloseDelayMs={4000}
        onAutoClose={finalizeAppointment}
      />

      <OkDialog
        visible={errorDialogVisible}
        message={errorDialogMessage}
        onOk={() => setErrorDialogVisible(false)}
      />
      <FullScreenLoader visible={isSubmitting} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  sectionHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  timeLabelSpacing: {
    marginBottom: 12,
  },
  slotLabelText: {
    fontFamily: "Matter-Medium",
    fontWeight: "500",
    fontSize: 12,
    lineHeight: 12,
    letterSpacing: 0,
  },
  dateScrollControls: {
    flexDirection: "row",
    gap: 8,
  },
  // ChevronLeftBadgeIcon/ChevronRightBadgeIcon are static SVGs — native RTL
  // auto-mirror swaps this row's order but not each icon's own pointing
  // direction, so Arabic showed "><" instead of "<>". direction:'ltr' keeps
  // the pair in its authored left-then-right order in both languages.
  dateScrollControlsRTL: {
    direction: "ltr",
  },
  scrollButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  dateScrollView: {
    height: 68,
    marginBottom: 16,
    flexGrow: 0,
    flexShrink: 0,
  },
  dateList: {
    gap: DATE_CHIP_GAP,
  },
  // Arabic: the scaleX flip below is the ONLY mirroring. The ambient root
  // direction:'rtl' would mirror the content row too (and Android's
  // horizontal ScrollView reverses its scroll coordinates under RTL), so
  // lock the strip to LTR — offsets then stay positive and identical on
  // both platforms, and the flip puts today on the right.
  dateScrollViewRTL: {
    direction: "ltr",
    transform: [{ scaleX: -1 }],
  },
  dateListRTL: {
    direction: "ltr",
  },
  dateCard: {
    width: DATE_CHIP_WIDTH,
    height: 68,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
  },
  timeGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: TIME_SLOT_GAP,
    justifyContent: "flex-start",
  },
  // direction:'ltr' so 'row-reverse' is the only reversal: under the root
  // direction:'rtl' alone, row-reverse double-flips and Arabic slots filled
  // left-to-right (earliest slot top-left instead of top-right).
  timeGridRTL: {
    direction: "ltr",
    flexDirection: "row-reverse",
  },
  timeCard: {
    flexGrow: 0,
    flexShrink: 0,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: "center",
  },
  timeCardPhone: {
    width: "31%",
  },
  timeCardDisabled: {
    opacity: 0.6,
  },
  timeCardSkeleton: {
    minWidth: "31%",
  },
  timeCardSkeletonFixed: {
    minWidth: TIME_SLOT_CHIP_WIDTH,
  },
  slotsStateBlock: {
    width: "100%",
    paddingVertical: 24,
    paddingHorizontal: 8,
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },
  // Fills the remaining space below "Slot Time" so the illustration sits
  // centered in the middle of the empty area, not bunched at the top.
  noSlotsWrapper: {
    flex: 1,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },
  // Figma: Font/Size/Base Regular, LH 22
  slotsStateText: {
    textAlign: "center",
    lineHeight: 22,
    letterSpacing: 0,
  },
  emptySlotsMessageRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  emptySlotsMessageRowRTL: {
    flexDirection: "row-reverse",
  },
  raiseSupportTicketText: {
    fontFamily: "Matter-Medium",
    fontWeight: "500",
    fontSize: 14,
    lineHeight: 13,
    letterSpacing: 0,
  },
  footer: {
    flexDirection: "row",
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 16,
    borderTopWidth: 1,
  },
  footerButton: {
    flex: 1,
    fontSize: 10,
  },
});

export default BookAppointmentScreen;
